import React, {
  RefObject,
  useEffect,
  useContext,
  useState,
  useMemo,
} from 'react';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useMedia,
  useSidePanel,
} from 'core/hooks';
import Table from 'core/components/VirtualTable';
import columns from 'pages/Dashboard/pages/Billing/Charges/columns/claimColumns';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import DataProvider, {
  DataContext,
} from 'core/context/data';
import clsx from 'clsx';
import {
  PaginationState as ReactTablePaginationState,
} from '@tanstack/react-table';
import useStorageState from 'core/hooks/storage';
import {
  toChargesPanelInfo,
} from 'pages/Dashboard/pages/Billing/Charges/chargesPanelInfo';
import {
  ClaimRowData,
} from 'pages/Dashboard/pages/Billing/types';
import {
  getClaimSubRows,
  normalizeClaims,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  ClaimDetailsResponseDTO,
  ClaimsResponseDTO,
} from 'dtos';
import {
  postApiChargeGetClaims,
  postApiChargeGetClaimDetailsByClaimIds,
} from 'endpoints';
import {
  buildClaimsRequest,
} from 'pages/Dashboard/pages/Billing/api';
import defer from 'lodash/defer';
import ClaimSelectionBanner from 'pages/Dashboard/pages/Billing/Charges/BulkClaim/ClaimSelectionBanner';
import uniq from 'lodash/uniq';
import {
  ClaimsBillingViewProps,
} from 'pages/Dashboard/pages/Billing/Charges';
import RowDetailsProvider, {
  useClaimDetails,
} from 'pages/Dashboard/pages/Billing/Charges/RowDetailsProvider';

function getRowId(row: ClaimRowData) {
  return row.rowKey
    ?? row.visitNoteBillingCodeId
    ?? row.claimId
    ?? 0;
}

type OverviewProps = {
  tableInstanceRef?: RefObject<VirtualTableInstance>;
  queryParams: Record<string, unknown>;
};

function Overview({ tableInstanceRef, queryParams }: OverviewProps) {
  const { requestMedia } = useMedia();
  const { openPanel } = useSidePanel();
  const { isFetched, data } = useContext(DataContext);
  const {
    detailsById,
  } = useClaimDetails();

  const [
    defaultPagination,
    setDefaultPagination,
  ] = useStorageState<ReactTablePaginationState>('claimsPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  const pagination = tableInstanceRef?.current?.pagination ?? defaultPagination ?? {};
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  useEffect(() => {
    setDefaultPagination(pagination);
  }, [pagination]);

  useEffect(() => {
    const unsubscribeFromChargesInfoChange = billingEventChannel.on('onChargesInfoChange', () => {
      tableInstanceRef?.current?.refetch?.();
    });

    const unsubscribeFromPaymentCollect = paymentsEventChannel.on('onPatientBalanceChange', () => {
      const { refetch } = tableInstanceRef?.current ?? {};
      refetch?.();
    });

    const unsubscribeFromClaimsRowsSelectionChange = billingEventChannel.on('onClaimsRowsSelectionChange', () => {
      defer(() => {
        const { rowSelection } = tableInstanceRef?.current ?? {};
        const ids = Object.keys(rowSelection ?? {});
        setSelectedRows(uniq(ids.map((id) => Number(id))));
      });
    });

    const unsubscribeFromBulkClaimStatusUpdated = billingEventChannel.on('onBulkClaimStatusUpdated', () => {
      tableInstanceRef?.current?.resetRowSelection?.();
      setSelectedRows([]);
    });

    return () => {
      unsubscribeFromChargesInfoChange();
      unsubscribeFromPaymentCollect();
      unsubscribeFromClaimsRowsSelectionChange();
      unsubscribeFromBulkClaimStatusUpdated();
    };
  }, []);

  useEffect(() => {
    tableInstanceRef?.current?.resetRowSelection?.();
    setSelectedRows([]);
  }, [queryParams]);

  useEffect(() => {
    if (isFetched) {
      requestMedia((data ?? [])?.map(({ patient }) => patient?.patientId));
    }
  }, [isFetched, data]);

  const subRowsByClaimId = useMemo(() => {
    const subRows = new Map<number, ReturnType<typeof getClaimSubRows>>();
    (data ?? []).forEach((row: ClaimRowData) => {
      const claimId = row.claimId ?? 0;
      subRows.set(
        claimId,
        getClaimSubRows(row, detailsById[claimId]),
      );
    });
    return subRows;
  }, [data, detailsById]);

  return (
    <div className="relative flex min-w-0 flex-1 flex-col overflow-y-hidden">
      <ClaimSelectionBanner selectedRows={selectedRows} />
      <div className="relative flex min-w-0 flex-1 flex-col overflow-y-hidden charges-table-container">
        <div className="w-full h-full">
          <Table<ClaimRowData>
            instanceRef={tableInstanceRef}
            getSubRows={(row) => (
              row.isChild
                ? undefined
                : subRowsByClaimId.get(row.claimId ?? 0)
            )}
            getRowCanExpand={(row) => row.original?.expandable ?? false}
            skeleton={{ rows: 8 }}
            columns={columns}
            onRowClick={async (table, row) => {
              openPanel('charges', toChargesPanelInfo({
                encounterId: row.original?.encounterId,
                patientId: row.original?.patientId,
                visitDate: row.original?.visitDateTime,
                claimId: row.original?.claimId,
                tab: 'charge',
              }));
            }}
            getRowId={getRowId}
            classes={{
              header: '!py-0.5 px-4 !items-end',
              outerColumn: '!items-start',
              column: '!text-left',
              scrollContainer: 'justify-center items-center',
              outerRow: '!w-[calc(100%-16px)] left-4',
              row: '!items-start',
              cell: '!items-start',
              subRow: ({ original: { isClaim } }) => clsx('!items-end', {
                '!bg-gray-100 !py-1': isClaim,
                '!pb-2 !border-t !border-gray-200': !isClaim,
              }),
            }}
            loadingSlot="Loading Charges..."
          />
        </div>
      </div>
    </div>
  );
}

export default function ClaimsView({
  dateRange,
  searchQuery,
  filterQuery,
  claimView,
  tableInstanceRef,
}: ClaimsBillingViewProps) {
  const [defaultPagination] = useStorageState<ReactTablePaginationState>('claimsPagination', {
    pageSize: 25,
    pageIndex: 0,
  });

  return (
    <DataProvider<ClaimsResponseDTO, ClaimRowData>
      queryKey="claims"
      pagination={defaultPagination}
      getData={postApiChargeGetClaims}
      getMeta={(response) => (response?.claims?.pageProperties ?? {})}
      getQuery={({
        dateRange,
        searchQuery,
        filterQuery,
        claimView,
        pagination,
      }) => ({
        ...buildClaimsRequest({
          dateRange,
          searchQuery,
          filterQuery,
          pagination,
        }),
        claimView,
      })}
      normalize={normalizeClaims}
      params={{
        dateRange,
        searchQuery,
        filterQuery,
        claimView,
      }}
    >
      <RowDetailsProvider<ClaimDetailsResponseDTO>
        queryKey="claim-details"
        fetchDetails={(ids) => postApiChargeGetClaimDetailsByClaimIds({
          claimIds: ids,
        })}
        getDetailsId={(details) => details.claimId ?? 0}
      >
        <Overview
          tableInstanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
          queryParams={{
            dateRange,
            searchQuery,
            filterQuery,
            claimView,
          }}
        />
      </RowDetailsProvider>
    </DataProvider>
  );
}
